/**
 * Geometry for a week grid: the Scheduler's and the centre agenda's.
 *
 * Kept out of the component so the arithmetic that decides where a block sits
 * — and how two bookings at the same hour share the column — can be tested
 * without rendering anything.
 */

/** `HH:MM` or `HH:MM:SS` → minutes since midnight. */
export function minutesOfDayTime(time: string): number {
    const [hours, minutes] = time.split(":").map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0;
    return hours * 60 + minutes;
}

export interface WeekSpan {
    startMinute: number;
    endMinute: number;
}

export interface WeekEventPlacement {
    /** Index into the array handed in, so the caller can pair it back up. */
    index: number;
    /** Column this event takes inside its overlapping group, 0-based. */
    lane: number;
    /** How many columns that group is split into. */
    lanes: number;
}

/**
 * Split a day's events into side-by-side lanes, Google Calendar style.
 *
 * Events that never overlap keep the full column width; a run of overlapping
 * events is divided into as many lanes as the busiest moment in that run needs,
 * so nothing is ever drawn on top of something else.
 */
export function layoutDayEvents(
    events: readonly WeekSpan[]
): WeekEventPlacement[] {
    const ordered = events
        .map((event, index) => ({ ...event, index }))
        .sort(
            (a, b) =>
                a.startMinute - b.startMinute || a.endMinute - b.endMinute
        );

    const placements: WeekEventPlacement[] = [];
    let pending: { index: number; lane: number }[] = [];
    let laneEnds: number[] = [];
    let groupEnd = Number.NEGATIVE_INFINITY;

    const flush = () => {
        const lanes = Math.max(laneEnds.length, 1);
        pending.forEach((placement) => placements.push({ ...placement, lanes }));
        pending = [];
        laneEnds = [];
    };

    ordered.forEach((event) => {
        // A start at or after every open end means the previous run is closed,
        // so its width is now known and the next run starts from one lane.
        if (event.startMinute >= groupEnd) flush();

        let lane = laneEnds.findIndex((end) => end <= event.startMinute);
        if (lane === -1) {
            laneEnds.push(event.endMinute);
            lane = laneEnds.length - 1;
        } else {
            laneEnds[lane] = event.endMinute;
        }

        groupEnd = Math.max(groupEnd, event.endMinute);
        pending.push({ index: event.index, lane });
    });
    flush();

    return placements.sort((a, b) => a.index - b.index);
}

/**
 * Whether a span on `day` has already finished.
 *
 * Compared against the therapist's own clock — `todayKey` and `nowMinute` are
 * read in their timezone — so a session is "over" when it is over for them,
 * not for whichever zone the browser happens to sit in.
 */
export function isSpanPast(
    day: string,
    span: WeekSpan,
    todayKey: string,
    nowMinute: number
): boolean {
    if (day < todayKey) return true;
    if (day > todayKey) return false;
    return span.endMinute <= nowMinute;
}

/** Overlapping or touching spans folded into one, ascending. */
export function mergeSpans(spans: readonly WeekSpan[]): WeekSpan[] {
    const ordered = [...spans].sort((a, b) => a.startMinute - b.startMinute);
    const merged: WeekSpan[] = [];

    ordered.forEach((span) => {
        const last = merged[merged.length - 1];
        if (last && span.startMinute <= last.endMinute) {
            last.endMinute = Math.max(last.endMinute, span.endMinute);
            return;
        }
        merged.push({ ...span });
    });

    return merged;
}

/**
 * The bookable gaps: working hours with the day's bookings cut out of them.
 *
 * `notBefore` drops what has already gone — time left this morning is free,
 * time before now is not, and marking it as free would invite a click that the
 * booking modal then refuses.
 */
export function freeSpans(
    windows: readonly WeekSpan[],
    busy: readonly WeekSpan[],
    notBefore = Number.NEGATIVE_INFINITY
): WeekSpan[] {
    const blocked = mergeSpans(busy);
    const free: WeekSpan[] = [];

    mergeSpans(windows).forEach((window) => {
        let cursor = Math.max(window.startMinute, notBefore);

        blocked.forEach((taken) => {
            if (taken.endMinute <= cursor) return;
            if (taken.startMinute >= window.endMinute) return;
            if (taken.startMinute > cursor) {
                free.push({
                    startMinute: cursor,
                    endMinute: Math.min(taken.startMinute, window.endMinute),
                });
            }
            cursor = Math.max(cursor, taken.endMinute);
        });

        if (cursor < window.endMinute) {
            free.push({ startMinute: cursor, endMinute: window.endMinute });
        }
    });

    return free.filter((span) => span.endMinute > span.startMinute);
}

export const DEFAULT_DAY_START_HOUR = 8;
export const DEFAULT_DAY_END_HOUR = 21;

/**
 * The hour range the grid renders.
 *
 * Starts from the usual working day and widens — never narrows — until every
 * span fits, because a booking outside the configured hours must still be
 * visible rather than clipped off the top or bottom of the grid.
 */
export function visibleHourRange(
    spans: readonly WeekSpan[]
): { startHour: number; endHour: number } {
    let startHour = DEFAULT_DAY_START_HOUR;
    let endHour = DEFAULT_DAY_END_HOUR;

    spans.forEach((span) => {
        startHour = Math.min(startHour, Math.floor(span.startMinute / 60));
        endHour = Math.max(endHour, Math.ceil(span.endMinute / 60));
    });

    return {
        startHour: Math.max(0, startHour),
        endHour: Math.min(24, Math.max(endHour, startHour + 1)),
    };
}
